import { Suspense, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { TopNav } from './TopNav';
import { MobileHeader } from './MobileHeader';
import { ErrorBoundary, LoadingScreen } from '@/components/ui';
import { useAuthStore } from '@/store/authStore';
import { usePlatformViewStore } from '@/store/platformViewStore';
import { useApprovalsRealtime } from '@/hooks';
import { cn } from '@/utils/cn';

export function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { platformAdmin } = useAuthStore();
  const { viewingOrgId, viewingOrgName, exitOrg } = usePlatformViewStore();
  // Every platform admin role is read-only when visiting (DECISION_LOG.md
  // Decision 001) — no role-conditional banner text needed anymore.
  const isVisiting = !!(platformAdmin && viewingOrgId);

  // Mounted app-wide (not just MyApprovalsPage) so the sidebar's pending-
  // approval badge and the dashboard's stat card both stay live (T037).
  useApprovalsRealtime();

  const handleExit = () => {
    exitOrg();
    navigate('/platform');
  };

  return (
    <div className="min-h-screen bg-surface-page">
      <MobileHeader onMenuClick={() => setMobileOpen(true)} />
      <Sidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />

      <div className={cn(collapsed ? 'md:pl-20' : 'md:pl-64', 'print:pl-0')} style={{ transition: 'padding-left 300ms ease-in-out' }}>
        {isVisiting && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-warning bg-warning-light px-5 py-2 text-sm text-warning-text print:hidden md:px-6">
            <span>
              Viewing <strong>{viewingOrgName}</strong> as Platform Admin — read-only access
            </span>
            <button
              type="button"
              onClick={handleExit}
              className="font-medium underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
            >
              Exit
            </button>
          </div>
        )}
        <TopNav />
        <main className="mx-auto max-w-7xl px-4 py-6 print:max-w-none print:px-0 print:py-0 md:px-6">
          <ErrorBoundary>
            <Suspense fallback={<LoadingScreen fullScreen={false} />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
